import { createHmac } from "node:crypto";
import { FUNNEL_STAGES, funnelStageEventTimes, type FunnelThreadFacts } from "./funnel";
import { DAY_MS } from "./window";

/**
 * B5 (BUILD-revenue-2026-10-11, ruling 6): the pure core of the randomized
 * holdout. Three jobs, each one deterministic code and nothing else:
 *
 * 1. Assignment. A keyed HMAC-SHA256 of (client, contact, experiment) picks
 *    one of 10,000 buckets, and the registered basis-point shares map buckets
 *    to arms. Same key and same unit, same arm, forever: no clock, no
 *    Math.random, no state.
 * 2. Sample size. The two-proportion formula, from the registered baseline
 *    rate, minimum detectable effect, alpha and power. The build list says
 *    "Code computes it; nothing else may", so a registration carries no
 *    sample size field at all; the analysis recomputes it here.
 * 3. Analysis. Intention-to-treat by ASSIGNED arm, a Newcomb hybrid Wilson
 *    interval on treatment minus control, and a verdict that refuses to call
 *    a result past a guardrail, on thin outcome tagging, or below the sample.
 *
 * Nothing here reads the environment. The assignment key arrives as bytes the
 * caller read through `envValue` under a credential NAME (CLAUDE.md section
 * 10: names in code, values in env), and no error message ever carries it.
 * The contact reference should be the pseudonymized token (CUST_0417), never
 * a raw handle.
 *
 * T3 (CLAUDE.md section 3): nothing here weighs outcomes or tunes itself. The
 * objective is the registered primary metric, fixed at registration, and the
 * learner never edits it.
 */

export class ExperimentError extends Error {
  constructor(message: string) {
    super(`experiment: ${message}`);
    this.name = "ExperimentError";
  }
}

function refuse(message: string): never {
  throw new ExperimentError(message);
}

/** The bucket space. Arm shares are integer basis points of it. */
export const EXPERIMENT_BUCKETS = 10_000;

/** 32 bytes, 256 bits: the HMAC-SHA256 block-safe minimum this module accepts. */
export const EXPERIMENT_KEY_MIN_BYTES = 32;

/**
 * Minimum outcome-tag coverage, in percent, below which an arm's data is
 * `invalid` rather than evidence. AWAITING RATIFICATION (Kabir): 80 is the
 * build list's recommendation (B5, "rec 80%"). A registration may set a
 * stricter threshold, never a looser one.
 */
export const EXPERIMENT_MIN_TAG_COVERAGE_PCT = 80;

/**
 * Holdout (control arm) share of the first experiment, agent-drafted vs
 * owner-unassisted, in basis points. AWAITING RATIFICATION (Kabir): 2000 is
 * the build list's recommendation (B5, "rec 20%").
 */
export const FIRST_EXPERIMENT_HOLDOUT_BP = 2000;

/** An attribution window longer than this many days is refused. */
const MAX_ATTRIBUTION_WINDOW_DAYS = 365;

/** The least strict interval the verdict may rest on: 95%, whatever alpha says. */
const MAX_INTERVAL_ALPHA = 0.05;

/**
 * Primary metric: a funnel stage. Defined locally while B1 builds funnel.ts in
 * parallel; it should be tied to funnel.ts's stage type once that lands.
 */
export const EXPERIMENT_PRIMARY_METRICS = [
  "consult_booked",
  "deposit",
  "sold",
  "contact_captured",
  "consult_attended",
] as const;
export type ExperimentPrimaryMetric = (typeof EXPERIMENT_PRIMARY_METRICS)[number];

/** The guardrails the build list names. Every registration sets a maximum for each. */
export const EXPERIMENT_GUARDRAILS = ["complaints", "unsubscribes", "verifier_holds", "ai_tell_hits"] as const;
export type ExperimentGuardrail = (typeof EXPERIMENT_GUARDRAILS)[number];

export type ArmRole = "treatment" | "control";

// ---------------------------------------------------------------------------
// Small validators
// ---------------------------------------------------------------------------

function isNonNegInt(n: unknown): n is number {
  return typeof n === "number" && Number.isSafeInteger(n) && n >= 0;
}

function isOpenUnit(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n) && n > 0 && n < 1;
}

function nonEmpty(s: unknown, what: string): string {
  if (typeof s !== "string" || s.trim() === "") refuse(`${what} must be a non-empty string`);
  return s;
}

// ---------------------------------------------------------------------------
// 1. Assignment
// ---------------------------------------------------------------------------

export interface AssignmentUnit {
  readonly clientId: string;
  readonly contactRef: string;
  readonly experimentId: string;
}

export interface AssignableArm {
  readonly id: string;
  readonly shareBp: number;
}

/** Domain-separates this HMAC from any other use of the same key. */
const ASSIGNMENT_DOMAIN = "ackra.experiment.assign.v1";

/**
 * The HMAC message: the domain tag, then client, contact and experiment, each
 * as a 4-byte big-endian byte length followed by its UTF-8 bytes. Length
 * prefixes make the encoding injective, so ("a|b", "c") and ("a", "b|c")
 * can never produce the same message, whatever characters the ids contain.
 */
export function encodeAssignmentMessage(unit: AssignmentUnit): Buffer {
  const fields = [
    ASSIGNMENT_DOMAIN,
    nonEmpty(unit?.clientId, "clientId"),
    nonEmpty(unit?.contactRef, "contactRef"),
    nonEmpty(unit?.experimentId, "experimentId"),
  ];
  const parts: Buffer[] = [];
  for (const f of fields) {
    const bytes = Buffer.from(f, "utf8");
    const len = Buffer.alloc(4);
    len.writeUInt32BE(bytes.length, 0);
    parts.push(len, bytes);
  }
  return Buffer.concat(parts);
}

function keyBytes(key: Buffer | string): Buffer {
  const bytes = typeof key === "string" ? Buffer.from(key, "utf8") : key;
  if (!Buffer.isBuffer(bytes) || bytes.length === 0) refuse("assignment key is empty");
  if (bytes.length < EXPERIMENT_KEY_MIN_BYTES) {
    // The length is not the secret; the bytes are, and they are never echoed.
    refuse(`assignment key is ${bytes.length} bytes, below the ${EXPERIMENT_KEY_MIN_BYTES}-byte minimum`);
  }
  return bytes;
}

/**
 * The unit's bucket in [0, 10,000): the first 6 digest bytes as a big-endian
 * integer, modulo 10,000. The modulo bias is 2^48 mod 10,000 over 2^48, under
 * 4e-11 per bucket, far below anything an experiment of this size can see.
 */
export function assignmentBucket(key: Buffer | string, unit: AssignmentUnit): number {
  const digest = createHmac("sha256", keyBytes(key)).update(encodeAssignmentMessage(unit)).digest();
  return digest.readUIntBE(0, 6) % EXPERIMENT_BUCKETS;
}

function checkArmShares(arms: readonly AssignableArm[]): void {
  if (!Array.isArray(arms) || arms.length < 2) refuse("an experiment needs at least two arms");
  const ids = new Set<string>();
  let total = 0;
  for (const arm of arms) {
    const id = nonEmpty(arm?.id, "arm id");
    if (ids.has(id)) refuse(`arm id "${id}" is registered twice`);
    ids.add(id);
    if (!Number.isSafeInteger(arm.shareBp) || arm.shareBp <= 0) {
      refuse(`arm "${id}" share must be a positive integer of basis points`);
    }
    total += arm.shareBp;
  }
  if (total !== EXPERIMENT_BUCKETS) refuse(`arm shares sum to ${total} basis points, not exactly ${EXPERIMENT_BUCKETS}`);
}

/**
 * Sticky arm assignment. Arms take contiguous bucket ranges in REGISTERED
 * order, so reordering or resizing arms reassigns units: the registry must
 * freeze arms at start (B5 registry, a later wave).
 */
export function assignArm(
  key: Buffer | string,
  unit: AssignmentUnit,
  arms: readonly AssignableArm[],
): { readonly armId: string; readonly bucket: number } {
  checkArmShares(arms);
  const bucket = assignmentBucket(key, unit);
  let upper = 0;
  for (const arm of arms) {
    upper += arm.shareBp;
    if (bucket < upper) return { armId: arm.id, bucket };
  }
  // Unreachable once shares sum to EXPERIMENT_BUCKETS; loud if it ever is not.
  return refuse(`bucket ${bucket} fell outside every arm`);
}

// ---------------------------------------------------------------------------
// 2. Inverse normal CDF and sample size
// ---------------------------------------------------------------------------

function poly(coeffs: readonly number[], x: number): number {
  // Horner, highest degree first.
  let acc = 0;
  for (const c of coeffs) acc = acc * x + c;
  return acc;
}

// Wichura, M.J. (1988), Algorithm AS241 PPND16, Applied Statistics 37(3):477-484.
// Coefficients highest degree first. About 1e-16 relative accuracy.
const AS241_A = [
  2509.0809287301226727, 33430.575583588128105, 67265.770927008700853, 45921.953931549871457,
  13731.693765509461125, 1971.5909503065514427, 133.14166789178437745, 3.387132872796366608,
];
const AS241_B = [
  5226.495278852854561, 28729.085735721942674, 39307.89580009271061, 21213.794301586595867,
  5394.1960214247511077, 687.1870074920579083, 42.313330701600911252, 1.0,
];
const AS241_C = [
  7.7454501427834140764e-4, 0.0227238449892691845833, 0.24178072517745061177, 1.27045825245236838258,
  3.64784832476320460504, 5.7694972214606914055, 4.6303378461565452959, 1.42343711074968357734,
];
const AS241_D = [
  1.05075007164441684324e-9, 5.475938084995344946e-4, 0.0151986665636164571966, 0.14810397642748007459,
  0.68976733498510000455, 1.6763848301838038494, 2.05319162663775882187, 1.0,
];
const AS241_E = [
  2.01033439929228813265e-7, 2.71155556874348757815e-5, 0.0012426609473880784386, 0.026532189526576123093,
  0.29656057182850489123, 1.7848265399172913358, 5.4637849111641143699, 6.6579046435011037772,
];
const AS241_F = [
  2.04426310338993978564e-15, 1.4215117583164458887e-7, 1.8463183175100546818e-5, 7.868691311456132591e-4,
  0.0148753612908506148525, 0.13692988092273580531, 0.59983220655588793769, 1.0,
];

/** The standard normal quantile: z such that P(Z <= z) = p, for p strictly inside (0, 1). */
export function inverseNormalCdf(p: number): number {
  if (!isOpenUnit(p)) refuse(`inverseNormalCdf needs a probability strictly between 0 and 1, got ${p}`);
  const q = p - 0.5;
  if (Math.abs(q) <= 0.425) {
    const r = 0.180625 - q * q;
    return (q * poly(AS241_A, r)) / poly(AS241_B, r);
  }
  let r = Math.sqrt(-Math.log(q < 0 ? p : 1 - p));
  let x: number;
  if (r <= 5) {
    r -= 1.6;
    x = poly(AS241_C, r) / poly(AS241_D, r);
  } else {
    r -= 5;
    x = poly(AS241_E, r) / poly(AS241_F, r);
  }
  return q < 0 ? -x : x;
}

export interface SampleSizeInput {
  /** Control-arm rate the registration expects, strictly inside (0, 1). */
  readonly baselineRate: number;
  /** Minimum detectable ABSOLUTE difference in proportions, > 0. */
  readonly minDetectableEffect: number;
  /** Two-sided significance level, in (0, 0.5]. */
  readonly alpha: number;
  /** Power, in [0.5, 1). */
  readonly power: number;
}

function checkSampleSizeInput(input: SampleSizeInput): void {
  const { baselineRate: p1, minDetectableEffect: d, alpha, power } = input ?? ({} as SampleSizeInput);
  if (!isOpenUnit(p1)) refuse("baseline rate must be strictly between 0 and 1");
  if (typeof d !== "number" || !Number.isFinite(d) || d <= 0) refuse("minimum detectable effect must be a positive absolute difference");
  if (!(p1 + d < 1)) refuse("baseline rate plus the minimum detectable effect must stay below 1");
  if (typeof alpha !== "number" || !Number.isFinite(alpha) || alpha <= 0 || alpha > 0.5) refuse("alpha must be in (0, 0.5]");
  if (typeof power !== "number" || !Number.isFinite(power) || power < 0.5 || power >= 1) refuse("power must be in [0.5, 1)");
}

/**
 * Units needed PER ARM for a two-sided two-proportion test:
 *
 *   n = (z_{1-a/2} sqrt(2 pbar qbar) + z_{1-b} sqrt(p1 q1 + p2 q2))^2 / d^2
 *
 * with pbar = (p1 + p2) / 2, rounded UP. The test is two-sided, so both
 * p2 = p1 + d and p2 = p1 - d are evaluated (each only when it stays inside
 * (0, 1)) and the larger n governs: the conservative reading, identical to
 * the one-direction figure whenever p1 <= 0.5.
 */
export function sampleSizePerArm(input: SampleSizeInput): number {
  checkSampleSizeInput(input);
  const { baselineRate: p1, minDetectableEffect: d, alpha, power } = input;
  const zAlpha = inverseNormalCdf(1 - alpha / 2);
  const zBeta = inverseNormalCdf(power);
  let worst = 0;
  for (const p2 of [p1 + d, p1 - d]) {
    if (!isOpenUnit(p2)) continue;
    const pbar = (p1 + p2) / 2;
    const root = zAlpha * Math.sqrt(2 * pbar * (1 - pbar)) + zBeta * Math.sqrt(p1 * (1 - p1) + p2 * (1 - p2));
    worst = Math.max(worst, (root * root) / (d * d));
  }
  return Math.ceil(worst);
}

// ---------------------------------------------------------------------------
// 3. Interval and analysis
// ---------------------------------------------------------------------------

function checkProportion(x: number, n: number, which: string): void {
  if (!isNonNegInt(x) || !isNonNegInt(n) || n === 0 || x > n) {
    refuse(`${which} must be integer counts with 0 <= successes <= n and n > 0`);
  }
}

/** Wilson score interval for x successes in n, at quantile z. */
function wilson(x: number, n: number, z: number): { readonly lower: number; readonly upper: number } {
  const p = x / n;
  const z2 = z * z;
  const denom = 1 + z2 / n;
  const centre = (p + z2 / (2 * n)) / denom;
  const half = (z / denom) * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n));
  return { lower: centre - half, upper: centre + half };
}

export interface DifferenceInterval {
  /** p1 - p2, exact, never rounded. */
  readonly difference: number;
  readonly lower: number;
  readonly upper: number;
}

/**
 * Newcomb (1998) method 10, the hybrid score interval for p1 - p2 built from
 * each proportion's Wilson interval, no continuity correction:
 *
 *   L = D - sqrt((p1 - l1)^2 + (u2 - p2)^2)
 *   U = D + sqrt((u1 - p1)^2 + (p2 - l2)^2)
 *
 * `confidence` defaults to 0.95; z is computed here, never the rounded 1.96.
 */
export function newcombDifferenceInterval(x1: number, n1: number, x2: number, n2: number, confidence = 0.95): DifferenceInterval {
  checkProportion(x1, n1, "first proportion");
  checkProportion(x2, n2, "second proportion");
  if (!isOpenUnit(confidence)) refuse("confidence must be strictly between 0 and 1");
  const z = inverseNormalCdf(1 - (1 - confidence) / 2);
  const p1 = x1 / n1;
  const p2 = x2 / n2;
  const w1 = wilson(x1, n1, z);
  const w2 = wilson(x2, n2, z);
  const difference = p1 - p2;
  return {
    difference,
    lower: difference - Math.sqrt((p1 - w1.lower) ** 2 + (w2.upper - p2) ** 2),
    upper: difference + Math.sqrt((w1.upper - p1) ** 2 + (p2 - w2.lower) ** 2),
  };
}

export type MeasuredVerdict = "lift" | "no_detectable_difference" | "harm";
export type ExperimentVerdict = "halted" | "invalid" | "not_measured" | MeasuredVerdict;

/**
 * Decided on the exact bounds. A bound of exactly zero does not exclude zero,
 * and nothing is rounded first, so a rate is never rounded up into a finding.
 */
export function verdictFromInterval(lower: number, upper: number): MeasuredVerdict {
  if (!Number.isFinite(lower) || !Number.isFinite(upper) || lower > upper) refuse("interval bounds are not a valid interval");
  if (lower > 0) return "lift";
  if (upper < 0) return "harm";
  return "no_detectable_difference";
}

// ---------------------------------------------------------------------------
// 4. Registration
// ---------------------------------------------------------------------------

export interface ExperimentArm extends AssignableArm {
  readonly role: ArmRole;
}

export interface ExperimentRegistration {
  readonly experimentId: string;
  /** Points at the pre-registered hypothesis text, never the text itself. */
  readonly hypothesisTextId: string;
  /** Exactly two: one treatment, one control. Order fixes the bucket ranges. */
  readonly arms: readonly ExperimentArm[];
  /** Reached this funnel stage within the attribution window. */
  readonly primaryMetric: ExperimentPrimaryMetric;
  readonly attributionWindowDays: number;
  readonly baselineRate: number;
  readonly minDetectableEffect: number;
  readonly alpha: number;
  readonly power: number;
  /** Highest count each guardrail may reach in the treatment arm. Every guardrail, integers. */
  readonly guardrailMaxima: Readonly<Record<ExperimentGuardrail, number>>;
  /** Percent, at least EXPERIMENT_MIN_TAG_COVERAGE_PCT, at most 100. */
  readonly coverageThresholdPct: number;
}

function checkGuardrailRecord(record: unknown, what: string): Record<ExperimentGuardrail, number> {
  if (record === null || typeof record !== "object") refuse(`${what} must name every guardrail`);
  const keys = Object.keys(record);
  for (const k of keys) {
    if (!(EXPERIMENT_GUARDRAILS as readonly string[]).includes(k)) refuse(`${what} names an unknown guardrail "${k}"`);
  }
  for (const g of EXPERIMENT_GUARDRAILS) {
    const v = (record as Record<string, unknown>)[g];
    if (!isNonNegInt(v)) refuse(`${what} needs a non-negative integer for "${g}"`);
  }
  return record as Record<ExperimentGuardrail, number>;
}

/**
 * Refuses an incomplete or inconsistent registration loudly and returns the
 * sample size per arm the code computes from it.
 */
export function validateRegistration(reg: unknown): { readonly requiredPerArm: number } {
  if (reg === null || typeof reg !== "object") refuse("registration must be an object");
  const r = reg as ExperimentRegistration;
  nonEmpty(r.experimentId, "experimentId");
  nonEmpty(r.hypothesisTextId, "hypothesisTextId");
  if (!(EXPERIMENT_PRIMARY_METRICS as readonly string[]).includes(r.primaryMetric)) {
    refuse(`primary metric must be one of ${EXPERIMENT_PRIMARY_METRICS.join(", ")}`);
  }
  if (!Number.isSafeInteger(r.attributionWindowDays) || r.attributionWindowDays < 1 || r.attributionWindowDays > MAX_ATTRIBUTION_WINDOW_DAYS) {
    refuse(`attribution window must be a whole number of days from 1 to ${MAX_ATTRIBUTION_WINDOW_DAYS}`);
  }
  if (!Array.isArray(r.arms) || r.arms.length !== 2) refuse("a two-proportion experiment registers exactly two arms");
  checkArmShares(r.arms);
  const roles = r.arms.map((a) => a.role);
  if (!roles.includes("treatment") || !roles.includes("control")) refuse("the two arms must be one treatment and one control");
  checkGuardrailRecord(r.guardrailMaxima, "guardrail maxima");
  if (
    typeof r.coverageThresholdPct !== "number" ||
    !Number.isFinite(r.coverageThresholdPct) ||
    r.coverageThresholdPct < EXPERIMENT_MIN_TAG_COVERAGE_PCT ||
    r.coverageThresholdPct > 100
  ) {
    refuse(`coverage threshold must be from ${EXPERIMENT_MIN_TAG_COVERAGE_PCT} to 100 percent`);
  }
  return { requiredPerArm: sampleSizePerArm(r) };
}

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------

/**
 * One arm's counts. THE ANALYSED COHORT IS THE CLOSED-WINDOW UNITS (fix round 1, S1): only a unit whose
 * attribution window has closed has a settled outcome, so the sample-size gate, the tag coverage and the interval
 * all read `closed`, never `assigned`. Inside that cohort it is still intention to treat by ASSIGNED arm: an
 * untagged closed unit is a non-success, never dropped. Guardrails read every assigned unit (safety first: a
 * breach halts while windows are still open).
 */
export interface ArmObservation {
  readonly armId: string;
  /** Units ASSIGNED to this arm, whatever happened to them after (ITT). */
  readonly assigned: number;
  /** Assigned units whose attribution window has closed. The analysed cohort. */
  readonly closed: number;
  /** Closed-window units whose outcome is tagged inside their window, success or not. */
  readonly tagged: number;
  /** Closed-window units that reached the primary metric (or a later stage) inside their window. */
  readonly successes: number;
  /** Over every assigned unit, from its assignment on. */
  readonly guardrails: Readonly<Record<ExperimentGuardrail, number>>;
}

export interface GuardrailBreach {
  readonly guardrail: ExperimentGuardrail;
  readonly observed: number;
  readonly maximum: number;
}

export interface ExperimentAnalysis {
  readonly verdict: ExperimentVerdict;
  readonly requiredPerArm: number;
  /** Raw counts, registered order. No rates: nothing here is rounded. */
  readonly arms: ReadonlyArray<{
    readonly armId: string;
    readonly role: ArmRole;
    readonly assigned: number;
    readonly closed: number;
    readonly tagged: number;
    readonly successes: number;
  }>;
  readonly breaches: readonly GuardrailBreach[];
  readonly lowCoverage: readonly string[];
  readonly belowSampleSize: readonly string[];
  /** Treatment minus control. Null unless the verdict is a measured one. */
  readonly interval: DifferenceInterval | null;
}

/**
 * Intention-to-treat analysis. Every condition is reported; the verdict takes
 * the most cautious one, in this order:
 *
 *   halted        a treatment guardrail above its registered maximum
 *   invalid       outcome-tag coverage of the closed-window units under the threshold in either arm
 *   not_measured  either arm with fewer CLOSED-window units than the computed sample size (fix round 1, S1:
 *                 an assigned unit whose window is still open has no settled outcome and does not count)
 *   lift | no_detectable_difference | harm, from the interval
 *
 * The interval level is 1 - alpha, never less strict than 95%.
 */
export function analyzeExperiment(reg: ExperimentRegistration, observations: readonly ArmObservation[]): ExperimentAnalysis {
  const { requiredPerArm } = validateRegistration(reg);
  if (!Array.isArray(observations) || observations.length !== reg.arms.length) {
    refuse("observations must cover every registered arm exactly once");
  }
  const byArm = new Map<string, ArmObservation>();
  for (const o of observations) {
    if (!reg.arms.some((a) => a.id === o?.armId)) refuse(`observation for unregistered arm "${String(o?.armId)}"`);
    if (byArm.has(o.armId)) refuse(`arm "${o.armId}" observed twice`);
    if (!isNonNegInt(o.assigned) || !isNonNegInt(o.closed) || !isNonNegInt(o.tagged) || !isNonNegInt(o.successes)) {
      refuse(`arm "${o.armId}" counts must be non-negative integers`);
    }
    if (o.successes > o.tagged || o.tagged > o.closed || o.closed > o.assigned) {
      refuse(`arm "${o.armId}" needs successes <= tagged <= closed <= assigned`);
    }
    checkGuardrailRecord(o.guardrails, `arm "${o.armId}" guardrail counts`);
    byArm.set(o.armId, o);
  }

  const ordered = reg.arms.map((a) => ({ arm: a, obs: byArm.get(a.id)! }));
  const treatment = ordered.find((x) => x.arm.role === "treatment")!;
  const control = ordered.find((x) => x.arm.role === "control")!;

  const breaches: GuardrailBreach[] = [];
  for (const g of EXPERIMENT_GUARDRAILS) {
    const observed = treatment.obs.guardrails[g];
    const maximum = reg.guardrailMaxima[g];
    if (observed > maximum) breaches.push({ guardrail: g, observed, maximum });
  }
  // tagged / closed < threshold / 100, cross-multiplied so no rate is formed.
  const lowCoverage = ordered.filter((x) => x.obs.tagged * 100 < reg.coverageThresholdPct * x.obs.closed).map((x) => x.arm.id);
  // The gate counts closed-window units only (fix round 1, S1): no interval while outcomes are still coming in.
  const belowSampleSize = ordered.filter((x) => x.obs.closed < requiredPerArm).map((x) => x.arm.id);

  let verdict: ExperimentVerdict;
  let interval: DifferenceInterval | null = null;
  if (breaches.length > 0) verdict = "halted";
  else if (lowCoverage.length > 0) verdict = "invalid";
  else if (belowSampleSize.length > 0) verdict = "not_measured";
  else {
    const confidence = 1 - Math.min(reg.alpha, MAX_INTERVAL_ALPHA);
    interval = newcombDifferenceInterval(
      treatment.obs.successes,
      treatment.obs.closed,
      control.obs.successes,
      control.obs.closed,
      confidence,
    );
    verdict = verdictFromInterval(interval.lower, interval.upper);
  }

  return {
    verdict,
    requiredPerArm,
    arms: ordered.map(({ arm, obs }) => ({
      armId: arm.id,
      role: arm.role,
      assigned: obs.assigned,
      closed: obs.closed,
      tagged: obs.tagged,
      successes: obs.successes,
    })),
    breaches,
    lowCoverage,
    belowSampleSize,
    interval,
  };
}

export interface ExperimentUnitResult {
  /** `now` is past the end of the unit's attribution window, so its outcome is settled. */
  readonly windowClosed: boolean;
  /** A standing outcome recorded inside [assignedAt, assignedAt + window], or a success. */
  readonly tagged: boolean;
  /** A fact of the primary stage or a later one inside [assignedAt, assignedAt + window], both ends inclusive. */
  readonly success: boolean;
}

/**
 * One assigned unit (a contact and all its threads), read against ITS OWN window (fix round 1):
 *  - S3: success is ANY fact time of the primary stage or a later one inside the window, not the funnel's
 *    earliest-ever time, so a thread that booked before the assignment and books again inside the window counts.
 *    Reversed outcomes never count (`funnelStageEventTimes` reads standing outcomes only).
 *  - S2: tagged only by an outcome recorded at or after the unit's own assignment and inside the window; a tag
 *    from before the exposure says nothing about it.
 *  - S1: the window is closed once `now` is strictly past its end; only then does the unit join the analysed cohort.
 */
export function experimentUnitResult(input: {
  readonly primaryMetric: ExperimentPrimaryMetric;
  readonly attributionWindowDays: number;
  readonly assignedAt: Date;
  readonly now: Date;
  readonly threads: readonly FunnelThreadFacts[];
}): ExperimentUnitResult {
  const metricIndex = FUNNEL_STAGES.indexOf(input.primaryMetric);
  if (metricIndex < 0) refuse("the primary metric is not a funnel stage");
  if (!Number.isSafeInteger(input.attributionWindowDays) || input.attributionWindowDays < 1) {
    refuse("the attribution window must be a whole number of days");
  }
  const from = input.assignedAt.getTime();
  const to = from + input.attributionWindowDays * DAY_MS;
  if (!Number.isFinite(from) || !Number.isFinite(input.now.getTime())) refuse("the assignment time and now must be times");
  const inside = (d: Date): boolean => d.getTime() >= from && d.getTime() <= to;
  let success = false;
  let taggedByOutcome = false;
  for (const f of input.threads) {
    const times = funnelStageEventTimes(f);
    for (let s = metricIndex; s < FUNNEL_STAGES.length; s++) {
      if (times[FUNNEL_STAGES[s]!].some(inside)) success = true;
    }
    if (f.outcomes.some((o) => o.reversedAt === null && inside(o.at))) taggedByOutcome = true;
  }
  return { windowClosed: input.now.getTime() > to, tagged: success || taggedByOutcome, success };
}

// ---------------------------------------------------------------------------
// Registry vocabulary (B5, migration 0061)
// ---------------------------------------------------------------------------

/**
 * Why a started experiment stopped: a code, never prose. AWAITING RATIFICATION (Kabir): the closed list. A stop
 * is a person's act in this build (`pnpm experiment stop`); an automatic guardrail halt has no writer yet.
 */
export const EXPERIMENT_STOP_REASONS = ["owner_halt", "guardrail_breach", "concluded"] as const;
export type ExperimentStopReason = (typeof EXPERIMENT_STOP_REASONS)[number];

/** The env var NAMES the registry reads (CLAUDE.md section 10: names in code, values in the environment). */
export const EXPERIMENT_KEY_ENV = "ACKRA_EXPERIMENT_KEY";
export const EXPERIMENTS_FLAG_ENV = "ACKRA_ENABLE_EXPERIMENTS";

/**
 * Decodes the assignment key from the base64 of exactly 32 random bytes. Refuses loudly (never a default, never
 * the value in the message): unset or blank, not canonical base64, or not exactly 32 bytes after decoding.
 */
export function decodeExperimentKey(raw: string | undefined): Buffer {
  if (raw === undefined || raw.trim() === "") refuse(`${EXPERIMENT_KEY_ENV} is not set; there is no default key and no assignment happens without one`);
  const text = raw.trim();
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(text) || text.length % 4 !== 0) refuse(`${EXPERIMENT_KEY_ENV} is not base64`);
  const bytes = Buffer.from(text, "base64");
  if (bytes.toString("base64") !== text) refuse(`${EXPERIMENT_KEY_ENV} is not canonical base64`);
  if (bytes.length !== EXPERIMENT_KEY_MIN_BYTES) {
    refuse(`${EXPERIMENT_KEY_ENV} decodes to ${bytes.length} bytes; it must be the base64 of exactly ${EXPERIMENT_KEY_MIN_BYTES} random bytes`);
  }
  return bytes;
}
